<template>
  <div class="organizations-view">
    <Toast />
    <div class="p-4">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold">Gerenciar Organizações</h1>
        <div class="flex gap-2">
          <button
            @click="openCreateDialog"
            class="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark"
          >
            Nova Organização
          </button>
          <Button
            v-if="orgs?.some(org => org.name === 'Cambio Hoje')"
            :label="isMigrating ? 'Migrando...' : 'Migrar Centros de Custo'"
            :icon="isMigrating ? 'pi pi-spin pi-spinner' : 'pi pi-copy'"
            :disabled="isMigrating"
            severity="info"
            @click="handleMigrateToCambioHoje"
          />
        </div>
      </div>

      <!-- Lista de Organizações -->
      <div class="organizations">
        <DataTable
          :value="orgs"
          :loading="loading"
          class="p-datatable-sm"
          :style="{ minWidth: '800px', zIndex: 1 }"
          v-model:selection="selectedOrganization"
          dataKey="id"
          @row-select="onRowSelect"
          @row-unselect="onRowUnselect"
          responsiveLayout="scroll"
          :paginator="orgs.length > 10"
          :rows="10"
          :rowsPerPageOptions="[5, 10, 20, 50]"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} organizações"
        >
          <template #empty>
            <div class="text-center p-4">
              <p v-if="loading">Carregando organizações...</p>
              <p v-else>Nenhuma organização encontrada</p>
            </div>
          </template>

          <Column field="name" header="Nome">
            <template #body="{ data }">
              <div class="flex items-center gap-2">
                <div v-if="data.logo" class="w-10 h-10">
                  <img :src="data.logo" :alt="data.name" class="w-full h-full object-cover rounded-full" />
                </div>
                <div v-else class="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                  {{ data.name.charAt(0).toUpperCase() }}
                </div>
                <span>{{ data.name }}</span>
              </div>
            </template>
          </Column>
          
          <Column field="createdAt" header="Criado em">
            <template #body="{ data }">
              {{ new Date(data.createdAt).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }) }}
            </template>
          </Column>

          <Column header="Ações" :exportable="false" style="min-width:8rem">
            <template #body="{ data }">
              <div class="flex gap-2">
                <Button icon="pi pi-pencil" rounded outlined severity="info" aria-label="Editar" @click="openEditDialog(data)" />
                <Button icon="pi pi-trash" rounded outlined severity="danger" aria-label="Excluir" @click="confirmDelete(data)" />
              </div>
            </template>
          </Column>
        </DataTable>
      </div>

      <!-- Dialog para criar/editar organização -->
      <Dialog 
        v-model:visible="dialogVisible" 
        :header="editingOrganization ? 'Editar Organização' : 'Nova Organização'"
        :modal="true"
        class="p-fluid"
        :style="{ width: '450px' }"
      >
        <div class="field">
          <label for="name">Nome</label>
          <InputText 
            id="name" 
            v-model.trim="organizationData.name" 
            required 
            autofocus 
            :class="{ 'p-invalid': submitted && !organizationData.name }"
          />
          <small class="p-error" v-if="submitted && !organizationData.name">Nome é obrigatório.</small>
        </div>

        <div class="field">
          <label for="logo">Logo</label>
          <div class="flex flex-column gap-2">
            <img
              v-if="logoPreview || organizationData.logo"
              :src="logoPreview || organizationData.logo"
              alt="Logo Preview"
              class="w-10rem h-10rem object-fit-contain mb-2"
            />
            <FileUpload
              mode="basic"
              :auto="true"
              accept="image/*"
              :maxFileSize="1000000"
              @select="onFileSelect"
              chooseLabel="Escolher Logo"
              class="w-full"
            />
            <small>Tamanho máximo: 1MB. Formatos aceitos: JPG, PNG</small>
          </div>
        </div>

        <template #footer>
          <Button 
            label="Cancelar" 
            icon="pi pi-times" 
            text 
            @click="closeDialog"
          />
          <Button 
            label="Salvar" 
            icon="pi pi-check" 
            @click="saveOrganization"
            :loading="loading"
          />
        </template>
      </Dialog>

      <!-- Dialog de confirmação de exclusão -->
      <ConfirmDialog></ConfirmDialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useOrganizationStore } from '@/stores/organization'
import { useAuthStore } from '@/stores/auth'
import type { Organization } from '@/stores/organization'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { uploadFile } from '@/composables/useStorage'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import FileUpload from 'primevue/fileupload'
import ConfirmDialog from 'primevue/confirmdialog'
import { useCurrentOrganizationStore } from '@/stores/currentOrganization'
import { storeToRefs } from 'pinia'
import { migrateCostCenters } from '@/firebase/services/costCenters.service'
import Toast from 'primevue/toast'

// Store initialization
const organizationStore = useOrganizationStore()
const authStore = useAuthStore()
const currentOrgStore = useCurrentOrganizationStore()
const toast = useToast()
const confirm = useConfirm()

// Reactive references
const loading = ref(false)
const dialogVisible = ref(false)
const submitted = ref(false)
const editingOrganization = ref<Organization | null>(null)
const organizationData = ref({
  name: '',
  logo: ''
})
const logoPreview = ref<string | null>(null)
const logoFile = ref<File | null>(null)
const selectedOrganization = ref<Organization | null>(null)
const isMigrating = ref(false)

// Computed properties
const isAuthenticated = computed(() => {
  const authenticated = authStore.isAuthenticated
  console.log('[OrganizationsView] Estado de autenticação computado:', {
    authenticated,
    user: authStore.user,
    currentUser: authStore.currentUser
  })
  return authenticated
})

const orgs = computed(() => {
  console.log('[OrganizationsView] Organizações computadas:', organizationStore.organizations)
  return organizationStore.organizations
})

// Watch para mudanças no estado de autenticação
watch(() => authStore.isAuthenticated, (newValue) => {
  console.log('[OrganizationsView] Mudança no estado de autenticação:', newValue)
  if (newValue) {
    fetchOrganizations()
  }
})

// Watch para mudanças nas organizações
watch(() => organizationStore.organizations, (newValue) => {
  console.log('[OrganizationsView] Mudança nas organizações:', newValue)
}, { deep: true })

const checkAuth = () => {
  const authState = {
    isAuthenticated: isAuthenticated.value,
    user: authStore.user,
    userData: authStore.userData,
    currentUser: authStore.currentUser
  }
  console.log('[OrganizationsView] Verificando autenticação:', authState)

  if (!isAuthenticated.value) {
    console.error('[OrganizationsView] Usuário não autenticado')
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Você precisa estar autenticado para gerenciar organizações',
      life: 3000
    })
    return false
  }
  return true
}

const fetchOrganizations = async () => {
  if (!checkAuth()) return

  loading.value = true
  try {
    await organizationStore.fetchOrganizations()
    console.log('[OrganizationsView] Organizações carregadas:', orgs.value)
  } catch (error) {
    console.error('[OrganizationsView] Erro ao carregar organizações:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao carregar organizações',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  console.log('[OrganizationsView] Componente montado')
  await fetchOrganizations()
})

const onFileSelect = (event: any) => {
  const file = event.files[0]
  if (file) {
    if (file.size > 1000000) {
      toast.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'O arquivo deve ter no máximo 1MB',
        life: 3000
      })
      return
    }

    console.log('[OrganizationsView] Arquivo selecionado:', file)
    logoFile.value = file

    // Preview do arquivo
    const reader = new FileReader()
    reader.onload = (e) => {
      logoPreview.value = e.target?.result as string
      console.log('[OrganizationsView] Preview do logo gerado')
    }
    reader.onerror = (e) => {
      console.error('[OrganizationsView] Erro ao gerar preview:', e)
      toast.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao gerar preview do arquivo',
        life: 3000
      })
    }
    reader.readAsDataURL(file)
  }
}

const openCreateDialog = () => {
  if (!checkAuth()) return

  editingOrganization.value = null
  organizationData.value = {
    name: '',
    logo: ''
  }
  logoFile.value = null
  logoPreview.value = null
  dialogVisible.value = true
  submitted.value = false
}

const openEditDialog = (org: Organization) => {
  if (!checkAuth()) return

  editingOrganization.value = org
  organizationData.value = {
    name: org.name,
    logo: org.logo || ''
  }
  logoFile.value = null
  logoPreview.value = org.logo || ''
  dialogVisible.value = true
  submitted.value = false
}

const closeDialog = () => {
  dialogVisible.value = false
  submitted.value = false
  logoFile.value = null
  logoPreview.value = null
}

const saveOrganization = async () => {
  if (!checkAuth()) return

  submitted.value = true

  if (!organizationData.value.name) {
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Por favor, preencha todos os campos obrigatórios',
      life: 3000
    })
    return
  }

  loading.value = true
  try {
    let logoUrl = organizationData.value.logo

    // Upload do logo se houver um novo arquivo
    if (logoFile.value) {
      try {
        console.log('[OrganizationsView] Iniciando upload do logo:', logoFile.value)
        const path = 'organizations/logos'
        logoUrl = await uploadFile(path, logoFile.value)
        console.log('[OrganizationsView] Logo convertido para base64')
      } catch (uploadError: any) {
        console.error('[OrganizationsView] Erro no upload do logo:', uploadError)
        toast.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao processar o logo: ' + (uploadError.message || 'Erro desconhecido'),
          life: 5000
        })
        return
      }
    }

    const data = {
      name: organizationData.value.name,
      logo: logoUrl || ''
    }

    if (editingOrganization.value) {
      await organizationStore.updateExistingOrganization(editingOrganization.value.id, data)
      toast.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Organização atualizada com sucesso',
        life: 3000
      })
    } else {
      await organizationStore.createNewOrganization(data)
      toast.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Organização criada com sucesso',
        life: 3000
      })
    }

    closeDialog()
    await fetchOrganizations() // Usando a função local em vez da do store diretamente
  } catch (error: any) {
    console.error('[OrganizationsView] Erro ao salvar organização:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: error.message || 'Erro ao salvar organização',
      life: 5000
    })
  } finally {
    loading.value = false
  }
}

const confirmDelete = (org: Organization) => {
  if (!checkAuth()) return

  confirm.require({
    message: `Tem certeza que deseja excluir a organização "${org.name}"?`,
    header: 'Confirmar Exclusão',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Sim',
    rejectLabel: 'Não',
    accept: () => deleteOrganization(org.id)
  })
}

const deleteOrganization = async (id: string) => {
  if (!checkAuth()) return

  try {
    await organizationStore.deleteExistingOrganization(id)
    await organizationStore.fetchOrganizations()
    toast.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Organização excluída com sucesso!',
      life: 3000
    })
  } catch (error) {
    console.error('Erro ao excluir organização:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Erro ao excluir organização. Tente novamente.',
      life: 5000
    })
  }
}

const onRowSelect = (event: { data: Organization }) => {
  console.log('[OrganizationsView] Organização selecionada:', event.data)
  selectedOrganization.value = event.data
}

const onRowUnselect = () => {
  console.log('[OrganizationsView] Organização desselecionada')
  selectedOrganization.value = null
}

const handleMigrateToCambioHoje = async () => {
  try {
    isMigrating.value = true
    
    // Encontra a organização Cambio Hoje
    const cambioHoje = orgs.value.find(org => org.name === 'Cambio Hoje')
    if (!cambioHoje) {
      throw new Error('Organização Cambio Hoje não encontrada')
    }

    // Encontra a primeira organização que não é Cambio Hoje
    const sourceOrg = orgs.value.find(org => org.name !== 'Cambio Hoje')
    if (!sourceOrg) {
      throw new Error('Nenhuma organização fonte encontrada')
    }

    await migrateCostCenters(sourceOrg.id, cambioHoje.id)
    toast.add({ 
      severity: 'success', 
      summary: 'Sucesso', 
      detail: 'Centros de custo migrados com sucesso!', 
      life: 3000 
    })
  } catch (error) {
    console.error('Erro ao migrar centros de custo:', error)
    toast.add({ 
      severity: 'error', 
      summary: 'Erro', 
      detail: 'Erro ao migrar centros de custo: ' + (error instanceof Error ? error.message : 'Erro desconhecido'), 
      life: 5000 
    })
  } finally {
    isMigrating.value = false
  }
}
</script>

<style scoped>
.organizations {
  padding: 2rem;
  position: relative;
  overflow-x: auto;
}

:deep(.p-datatable) {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.p-dialog) {
  z-index: 1000;
}

:deep(.p-confirm-dialog) {
  z-index: 1100;
}

/* Ajuste para garantir que os dropdowns fiquem sobre o sidebar */
:deep(.p-dropdown-panel),
:deep(.p-datatable-wrapper) {
  z-index: 100 !important;
}

/* Ajuste para garantir que o overlay do dropdown fique sobre o sidebar */
:deep(.p-dropdown-overlay),
:deep(.p-component-overlay) {
  z-index: 99 !important;
}
</style>